import type { ElectronAPI } from '../../types/electron';
import { blobToBase64 } from './binaryUtils';
import { assertSafeRelativePath } from './fileIntegrity';

/**
 * Write a file to a FileSystemDirectoryHandle (web browsers)
 * Creates necessary subdirectories if they don't exist
 * @param fileName - The file name with relative path
 * @param blob - The blob to write
 * @param dirHandle - The root directory handle
 * @returns Promise that resolves when the file is written
 */
export async function writeFileInDirectory(
  fileName: string,
  blob: Blob,
  dirHandle: FileSystemDirectoryHandle
): Promise<void> {
  assertSafeRelativePath(fileName);
  const parts = fileName.split('/');
  const fileNameOnly = parts.pop()!;
  let currentDir = dirHandle;

  // Create necessary subdirectories
  for (const part of parts) {
    currentDir = await currentDir.getDirectoryHandle(part, { create: true });
  }

  // Write the file
  const fileHandle = await currentDir.getFileHandle(fileNameOnly, {
    create: true,
  });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(blob);
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    throw error;
  }
}

/**
 * Write a file using Electron API
 * Creates necessary parent directories if they don't exist
 * @param electronAPI - The Electron API instance
 * @param filePath - The full path where the file should be written
 * @param blob - The blob to write
 * @returns Promise that resolves when the file is written
 */
export async function writeFileInElectron(
  electronAPI: ElectronAPI,
  filePath: string,
  blob: Blob,
  exclusive = false
): Promise<void> {
  if (exclusive && !electronAPI.writeFileExclusive)
    throw new Error(
      'Update the desktop app to safely save Blueprint downloads.'
    );
  // Convert blob to base64 for Electron writeFile
  const base64 = await blobToBase64(blob);

  // Ensure directory exists (create parent directories if needed)
  const parts = filePath.split('/');
  if (parts.length > 1) {
    const dirParts = parts.slice(0, -1);
    let currentPath = parts[0] || '';
    for (let i = 1; i < dirParts.length; i++) {
      currentPath = `${currentPath}/${dirParts[i]}`;
      // Check if directory exists, create if not
      try {
        const dirInfo = await electronAPI.getFileInfo(currentPath);
        if (!dirInfo.success || !dirInfo.info?.isDirectory) {
          // Directory doesn't exist, but we can't create it via Electron API
          // We'll let the writeFile handle the path creation
        }
      } catch {
        // Directory check failed, continue anyway
      }
    }
  }

  // Write the file
  const writeResult = exclusive
    ? await electronAPI.writeFileExclusive!(filePath, base64)
    : await electronAPI.writeFile(filePath, base64, 'base64');
  if (!writeResult.success) {
    throw new Error(writeResult.error || 'Failed to write file');
  }
}
